import { useState } from 'react';
import { useParams, Link } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PortfolioTab } from '../components/PortfolioTab';
import { SuiteIntakeTab } from '../components/SuiteIntakeTab';
import { FileNotesTab } from '../components/FileNotesTab';
import { ComplianceTab } from '../components/ComplianceTab';
import { AdviceTab } from '../components/AdviceTab';
import { RichEditor } from '../components/RichEditor';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';
import { formatAUD, formatDateAU } from '../../../shared/format';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';
import type { ClientFullResponse, ClientWithCompliance, DependantRecord, TransactionRow } from '../../../shared/api';

type TabKey = 'profile' | 'portfolio' | 'transactions' | 'notes' | 'intake' | 'advice' | 'compliance' | 'documents';

export function ClientDetail() {
  const params = useParams<{ id: string }>();
  const [tab, setTab] = useState<TabKey>('profile');
  const { user } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['client', params.id, 'full'],
    queryFn: () => api<{ data: ClientFullResponse }>(`/api/clients/${params.id}/full`),
  });

  if (isLoading) return <QASkeleton variant="detail" />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load client</div>;

  const c = data.data.client;
  const portfolio = data.data.portfolio;

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-3 text-sm text-ink-mute">
        <Link to="/" className="hover:text-rust">Dashboard</Link>
        <span className="mx-2">/</span>
        <span>{c.firstName} {c.surname}</span>
      </div>

      {/* Sticky header */}
      <div className="qa-page-head -mx-6 lg:-mx-10 px-6 lg:px-10 mb-6 border-b border-hairline">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="qa-eyebrow mb-2">Client {c.segment ?? 'unsegmented'}</p>
            <h1 className="qa-h1">
              {c.firstName} {c.surname}
              {c.soaNeeded && <span className="qa-italic-accent ml-3">— SoA outstanding.</span>}
            </h1>
            <p className="qa-mono text-xs text-ink-mute mt-2">
              {c.riskProfile ?? 'No risk profile'} · {c.suburb ?? '—'} {c.state ?? ''} · {c.email1 ?? '—'}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div className="qa-stat">
              <span className="qa-stat__label">Total Net Worth</span>
              <span className="qa-stat__value qa-mono">{formatAUD(portfolio?.tnw ?? 0, { compact: true })}</span>
            </div>
            <div className="qa-stat">
              <span className="qa-stat__label">Cash</span>
              <span className="qa-stat__value qa-mono">{formatAUD(portfolio?.cash ?? 0, { compact: true })}</span>
            </div>
            <div className="qa-stat">
              <span className="qa-stat__label">Review due</span>
              <span className="qa-stat__value qa-mono text-base">{formatDateAU(c.reviewDue)}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="qa-tabs mt-6 -mb-px">
          {([
            ['profile', 'Personal'],
            ['portfolio', 'Accounts & holdings'],
            ['transactions', 'Transactions'],
            ['notes', 'File notes'],
            ['intake', 'Suite intake'],
            ['advice', 'Advice documents'],
            ['compliance', 'Compliance'],
            ['documents', 'Documents'],
          ] as Array<[TabKey, string]>).map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`qa-tab ${tab === k ? 'qa-tab--active' : ''}`}
              aria-current={tab === k ? 'page' : undefined}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div>
        {tab === 'profile' && <ProfileTab client={c} dependants={data.data.dependants ?? []} canEdit={can(user?.role, 'client.edit')} />}
        {tab === 'portfolio' && <PortfolioTab portfolio={portfolio} />}
        {tab === 'transactions' && <TransactionsTab clientId={c.id} />}
        {tab === 'notes' && <FileNotesTab clientId={c.id} initial={data.data.recentNotes} />}
        {tab === 'intake' && <SuiteIntakeTab clientId={c.id} />}
        {tab === 'advice' && <AdviceTab clientId={c.id} canCreate={can(user?.role, 'advice.create')} canApprove={can(user?.role, 'advice.approve')} />}
        {tab === 'compliance' && <ComplianceTab client={c} canEdit={can(user?.role, 'client.edit')} />}
        {tab === 'documents' && <DocumentsTab />}
      </div>
    </div>
  );
}

/* --- profile tab (inline; everything else is its own component) --- */
function ProfileTab({ client: c, dependants, canEdit }: { client: ClientWithCompliance; dependants: DependantRecord[]; canEdit: boolean }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const mutate = useMutation({
    mutationFn: () => api(`/api/clients/${c.id}`, { method: 'PATCH', body: JSON.stringify(draft) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client', c.id] }),
  });

  function set(key: string, value: string) { setDraft(d => ({ ...d, [key]: value })); }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
      <Card title="Identity">
        <Field label="Title" value={c.title} />
        <Field label="First name" value={c.firstName} />
        <Field label="Surname" value={c.surname} />
        <Field label="Date of birth" value={formatDateAU(c.dateOfBirth)} />
        <Field label="Occupation" value={c.occupation} />
        <Field label="Employer" value={c.employer} />
      </Card>

      <Card title="Contact">
        <Field label="Email" value={c.email1} />
        <Field label="Email 2" value={c.email2} />
        <Field label="Mobile" value={c.phoneMobile} />
        <Field label="Home" value={c.phoneHome} />
        <Field label="Work" value={c.phoneWork} />
        <Field label="Address" value={[c.addressLine1, c.suburb, c.state, c.postcode].filter(Boolean).join(', ')} />
      </Card>

      <Card title="Profile">
        <Field label="Risk profile" value={c.riskProfile} />
        <Field label="Segment" value={c.segment} />
        <Field label="Acumen" value={c.acumenLevel} />
        <Field label="Tone" value={c.tonePreference} />
        <Field label="Retirement" value={formatDateAU(c.retirementDate)} />
      </Card>

      {dependants.length > 0 && (
        <Card title="Dependants" className="lg:col-span-3">
          <ul className="divide-y divide-hairline-paper -my-1">
            {dependants.map((d) => (
              <li key={d.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2 text-sm">
                <span className="text-ink">{[d.firstName, d.surname].filter(Boolean).join(' ') || '—'}</span>
                {d.relationship && <span className="qa-eyebrow text-xs">{d.relationship}</span>}
                {d.dateOfBirth && <span className="text-ink-mute qa-mono text-xs">DOB {formatDateAU(d.dateOfBirth)}</span>}
                {d.sourceApp && <span className="text-ink-mute text-xs ml-auto">via {d.sourceApp}</span>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card title="Goals & strategy" className="lg:col-span-3">
        {canEdit ? (
          <>
            <label className="qa-label">Goals / objectives</label>
            <RichEditor value={c.goalsObjectives ?? ''} onChange={v => set('goalsObjectives', v)} placeholder="What does this client want to achieve?" />
            <label className="qa-label mt-4">Strategy notes</label>
            <RichEditor value={c.strategyNotes ?? ''} onChange={v => set('strategyNotes', v)} placeholder="Strategy decisions, alternatives considered, rationale" />
            <label className="qa-label mt-4">Personal history</label>
            <RichEditor value={c.personalHistory ?? ''} onChange={v => set('personalHistory', v)} placeholder="Background, family, professional history" />
            <div className="flex justify-end gap-3 mt-4">
              <button className="btn-primary" disabled={mutate.isPending || Object.keys(draft).length === 0} onClick={() => mutate.mutate()}>
                {mutate.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </>
        ) : (
          <>
            <RichDisplay label="Goals" value={c.goalsObjectives} />
            <RichDisplay label="Strategy" value={c.strategyNotes} />
            <RichDisplay label="Personal history" value={c.personalHistory} />
          </>
        )}
      </Card>
    </div>
  );
}

function Card({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={`qa-card p-6 ${className}`}>
      <p className="qa-eyebrow mb-4">{title}</p>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, value, multiline }: { label: string; value?: string | number | null; multiline?: boolean }) {
  return (
    <div>
      <label className="qa-label">{label}</label>
      {multiline ? (
        <p className="text-sm text-ink whitespace-pre-wrap">{value ?? '—'}</p>
      ) : (
        <p className="text-sm text-ink">{value ?? '—'}</p>
      )}
    </div>
  );
}

function TransactionsTab({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['client', clientId, 'transactions'],
    queryFn: () => api<{ data: TransactionRow[] }>(`/api/clients/${clientId}/transactions`),
  });
  if (isLoading) return <div className="mt-6"><QASkeleton variant="table" rows={6} /></div>;
  const rows = data?.data ?? [];
  if (rows.length === 0) return <QAEmpty inline title="No transactions yet." />;
  return (
    <div className="overflow-x-auto border border-hairline mt-6">
      <table className="qa-table">
        <thead>
          <tr>
            <th>Trade date</th><th>Type</th><th>Security</th>
            <th className="text-right">Units</th><th className="text-right">Price</th>
            <th className="text-right">Consideration</th><th className="text-right">Brokerage</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id}>
              <td>{formatDateAU(t.trade_date)}</td>
              <td><span className={`qa-badge ${t.contract_type === 'buy' ? 'qa-badge--brass' : 'qa-badge--out'}`}>{t.contract_type.toUpperCase()}</span></td>
              <td>{t.security_code} <span className="text-ink-mute">{t.security_name}</span></td>
              <td className="num">{Number(t.units).toLocaleString('en-AU')}</td>
              <td className="num">{formatAUD(Number(t.price_cents) / 100)}</td>
              <td className="num">{formatAUD(Number(t.consideration_cents) / 100)}</td>
              <td className="num">{formatAUD(Number(t.brokerage_cents) / 100)}</td>
              <td>{t.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocumentsTab() {
  return (
    <div className="qa-card p-8 mt-6 text-center text-ink-mute">
      <p className="qa-eyebrow mb-3">Documents</p>
      <p>Local file-storage stub. SharePoint / OneDrive / Azure Blob adapters land in a follow-up phase.</p>
    </div>
  );
}

function RichDisplay({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <label className="qa-label">{label}</label>
      <div className="text-sm prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: value ?? '<p class="text-ink-mute">—</p>' }} />
    </div>
  );
}
